import { useState } from "react";
import { Link } from "react-router-dom";

import { hasErrors } from "@/lib/memberForms";
import { emptyDraft, draftErrors, FILTER_FEATURES } from "@/lib/v3Activities";
import { V3Title, V3Columns, V3Field, V3Actions, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard } from "../components/v3app";
import { SelectCard } from "../components/v3pass";
import { Unavailable, PrivacyNote } from "../components/kit";

const STEPS = ["Details", "Zugang", "Plätze", "Vorschau"];
const NEXT = ["Weiter zu Zugang", "Weiter zu Plätzen", "Weiter zur Vorschau"];
const STATE_OPTIONS = [["unknown", "Unbekannt"], ["present", "Vorhanden"], ["absent", "Nicht vorhanden"]];
const label = (v) => STATE_OPTIONS.find(([k]) => k === (v || "unknown"))[1];

function Steps({ step, onPick }) {
  return (
    <ol className="mb-[18px] flex gap-[4px] rounded-[10px] bg-v3-lilac p-[4px]" aria-label="Schritte" data-testid="activity-create-steps">
      {STEPS.map((s, i) => (
        <li key={s} className="flex-1">
          <button type="button" disabled={i > step} onClick={() => onPick(i)} aria-current={i === step ? "step" : undefined} aria-describedby={i > step ? "activity-create-steps-hint" : undefined} data-testid={`activity-create-step-${i}`}
            className={`flex min-h-[40px] w-full items-center justify-center rounded-[8px] px-2 text-[13px] transition-colors lg:text-[14px] ${i === step ? "bg-white font-bold text-v3-magenta shadow-sm" : "text-v3-muted enabled:hover:text-v3-ink disabled:cursor-default"}`}>{s}</button>
        </li>
      ))}
      <li className="sr-only" id="activity-create-steps-hint">Spätere Schritte sind erst nach dem vorherigen Schritt wählbar.</li>
    </ol>
  );
}

function When({ d, set, errors }) {
  return (
    <fieldset className="mb-[2px]"><legend className="mb-[12px] text-[14px] font-bold text-v3-ink lg:text-[15px]">Termin</legend>
      <div className="grid gap-x-[12px] sm:grid-cols-3">
        <V3Field id="activity-create-date" label="Datum" type="date" value={d.date} onChange={set("date")} error={errors.date} />
        <V3Field id="activity-create-from" label="Beginn" type="time" value={d.from} onChange={set("from")} />
        <V3Field id="activity-create-to" label="Ende" type="time" value={d.to} onChange={set("to")} error={errors.to} />
      </div>
    </fieldset>
  );
}

// M13: four steps as in the template; publishing needs POST /member/activities (C.5) and stays locked – nothing is stored.
export default function ActivityCreate() {
  const [step, setStep] = useState(0);
  const [d, setD] = useState(emptyDraft);
  const [errors, setErrors] = useState({});
  const set = (k) => (e) => { setD({ ...d, [k]: e.target.value }); setErrors({}); };
  const next = () => { const e = draftErrors(d, step); setErrors(e); if (!hasErrors(e)) setStep(step + 1); };
  const when = d.date && d.from ? `${d.date.split("-").reverse().join(".")} · ${d.from}${d.to ? `–${d.to}` : ""}` : "–";
  return (
    <div data-m="M13" data-testid="activity-create">
      <V3Title title="Aktivität erstellen" intro="Lege deine eigene Aktivität als Entwurf an." testid="activity-create-title" />
      <V3Columns layout="app" aside={<><AreaCard title="Ausgewählter Vorgang" text={d.title.trim() || "Neue Aktivität"} testid="activity-create-area" /><ResponsibleCard /></>} main={
        <form onSubmit={(e) => { e.preventDefault(); if (step < 3) next(); }} noValidate data-testid="activity-create-form">
          <Steps step={step} onPick={setStep} />
          {step === 0 && (<>
            <V3Field id="activity-create-title" label="Titel" value={d.title} onChange={set("title")} error={errors.title} maxLength={120} />
            <When d={d} set={set} errors={errors} />
            <V3Field id="activity-create-meeting" label="Öffentlicher Treffpunkt" value={d.meeting} onChange={set("meeting")} error={errors.meeting} hint="Zum Beispiel ein Café oder ein Bahnhofseingang – keine Wohnadresse." />
            <V3Field id="activity-create-cost" label="Kosten" value={d.cost} onChange={set("cost")} placeholder="Zum Beispiel: Keine Teilnahmegebühr" maxLength={120} />
          </>)}
          {step === 1 && (<div className="space-y-[11px]">
            <p className="text-[14px] text-v3-muted">Gib nur an, was du sicher weisst. „Unbekannt“ ist in Ordnung und wird nie als vorhanden gezeigt.</p>
            {FILTER_FEATURES.map(([k, l]) => <SelectCard key={k} id={`activity-create-access-${k}`} title={l} value={d.access[k] || "unknown"} onChange={(v) => setD({ ...d, access: { ...d.access, [k]: v } })} options={STATE_OPTIONS} testid={`activity-create-access-${k}`} />)}
          </div>)}
          {step === 2 && (<>
            <V3Field id="activity-create-seats" label="Teilnehmendenplätze" inputMode="numeric" value={d.seats} onChange={set("seats")} error={errors.seats} hint="Leer lassen für keine Begrenzung." />
            <V3Field id="activity-create-companions" label="Begleitplätze" inputMode="numeric" value={d.companions} onChange={set("companions")} error={errors.companions} hint="Werden getrennt von den Teilnehmendenplätzen bestätigt." />
          </>)}
          {step === 3 && (<div data-testid="activity-create-preview">
            <DetailList>
              <DetailRow label="Titel" value={d.title.trim()} testid="activity-create-preview-title" />
              <DetailRow label="Termin" value={when} testid="activity-create-preview-when" />
              <DetailRow label="Öffentlicher Treffpunkt" value={d.meeting.trim()} testid="activity-create-preview-meeting" />
              <DetailRow label="Kosten" value={d.cost.trim() || "Nicht angegeben"} testid="activity-create-preview-cost" />
              <DetailRow label="Zugang (eigene Angabe)" value={FILTER_FEATURES.map(([k, l]) => `${l}: ${label(d.access[k])}`).join(" · ")} testid="activity-create-preview-access" />
              <DetailRow label="Plätze" value={`${d.seats || "Ohne Begrenzung"} · Begleitplätze: ${d.companions || "keine"}`} testid="activity-create-preview-seats" />
            </DetailList>
            <div className="mt-[12px]"><Unavailable testid="activity-create-blocked">Eigene Aktivitäten veröffentlichen kann der gemeinsame Core noch nicht. Der Entwurf wird nicht gespeichert und geht beim Verlassen verloren.</Unavailable></div>
          </div>)}
          <V3Actions>
            {step < 3 ? <button type="submit" data-testid="activity-create-next" className={V3_PRIMARY}>{NEXT[step]}</button>
              : <button type="button" disabled aria-describedby="activity-create-blocked" data-testid="activity-create-publish" className={V3_PRIMARY}>Veröffentlichen</button>}
            {step === 0 ? <Link to="/aktivitaeten" data-testid="activity-create-cancel" className={V3_SECONDARY}>Abbrechen</Link>
              : <button type="button" onClick={() => setStep(step - 1)} data-testid="activity-create-back" className={V3_SECONDARY}>Zurück</button>}
          </V3Actions>
          <div className="mt-[18px]"><PrivacyNote>Teilnehmende melden sich an; ihr Access Pass wird dabei nicht geteilt.</PrivacyNote></div>
        </form>
      } />
    </div>
  );
}
